/**
 * LoanKpiTile — borrower Loans-list KPI tile.
 *
 * 5-column KPI strip on the Loans list page. Each tile shows an icon + value +
 * label using `baseColors` tokens for the accent stripe — no hardcoded hex.
 *
 * Subordinate to the page title — value caps at `body1` (17px Apple HIG),
 * label at `caption` (13px). See portals.md §11b2 typography hierarchy.
 */

'use client';

import { Box, baseColors, GlassCard, iconSizes, type SvgIconProps, Typography } from '@ops-matrix/design-system-ui';
import type { ComponentType, ReactElement } from 'react';

export type LoanKpiTone = 'amber' | 'blue' | 'mint' | 'orange' | 'id';

// Tone → palette key. We rely on the semantic aliases present in `baseColors`
// (warning/info/teal) plus indigo as a purple stand-in to stay forward-compat
// with the design-system-ui scale even if a build hasn't refreshed yet.
const TONE_COLOR: Record<LoanKpiTone, string> = {
  amber: baseColors.warning[400],
  blue: baseColors.info[400],
  mint: baseColors.teal[400],
  orange: baseColors.warning[600],
  id: baseColors.indigo[400],
};

export interface LoanKpiTileProps {
  label: string;
  value: string | number;
  /** Optional sub-line shown beneath the value (e.g. "CA · NY · TX"). Truncates with ellipsis. */
  sub?: string;
  tone: LoanKpiTone;
  icon: ComponentType<SvgIconProps>;
  loading?: boolean;
}

export function LoanKpiTile({ label, value, sub, tone, icon: Icon, loading }: LoanKpiTileProps): ReactElement {
  const accent = TONE_COLOR[tone];
  return (
    <GlassCard sx={{ p: 1.75, minWidth: 0, position: 'relative', overflow: 'hidden' }}>
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 2,
          bgcolor: accent,
        }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1 }}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'action.hover',
          }}
        >
          <Icon sx={{ fontSize: iconSizes.md, color: accent }} />
        </Box>
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', fontWeight: 500, letterSpacing: 0.2, lineHeight: 1.2 }}
        >
          {label}
        </Typography>
      </Box>
      <Typography
        variant="body1"
        sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}
      >
        {loading ? '—' : value}
      </Typography>
      {sub && !loading && (
        <Typography
          variant="caption"
          noWrap
          sx={{ display: 'block', mt: 0.5, color: 'text.secondary', lineHeight: 1.2 }}
        >
          {sub}
        </Typography>
      )}
    </GlassCard>
  );
}
